import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchIcon from "@mui/icons-material/Search";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pageHeroShellSx,
  pagePrimaryButtonSx,
  pageFieldSx,
} from "../../styles/pageModernStyles";
import { getUserById } from "../../api/User/userApi";
import {
  deleteSkillFicha,
  listSkillFichas,
} from "../../api/Skills/skillFichasApi";
import { statusDisplay } from "./skillFichaHelpers";

export default function SkillsPage() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contentMargin, isMobile } = useContext(SidebarContext);

  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }
      const decoded = jwtDecode(token);
      const user = await getUserById(decoded.userId);
      if (!user?.Permiso_Skills) {
        setForbidden(true);
        return;
      }
      const data = await listSkillFichas(q.trim() ? { q: q.trim() } : {});
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) setForbidden(true);
      else setError(t("skillAgent.fichaList.loadError"));
    } finally {
      setLoading(false);
    }
  }, [navigate, q, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (rowId) => {
    if (!window.confirm(t("skillAgent.fichaList.deleteConfirm"))) return;
    try {
      await deleteSkillFicha(rowId);
      setRows((prev) => prev.filter((r) => r.id !== rowId));
    } catch (err) {
      console.error(err);
      setError(t("skillAgent.fichaList.deleteError"));
    }
  };

  if (forbidden) {
    return (
      <Box sx={{ ...contentMargin, p: 3 }}>
        <Alert severity="warning">{t("skillAgent.fichaList.forbidden")}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        minHeight: "100dvh",
        width: "100%",
        maxWidth: 1200,
        pt: { xs: 1.25, md: 2 },
        px: { xs: 1.25, md: 3 },
        pb: { xs: 10, md: 3 },
        ...contentMargin,
      }}
    >
      <Box sx={pageHeroShellSx(theme, { sticky: isMobile })}>
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                }}
              >
                <AutoAwesomeOutlinedIcon />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={800}>
                  {t("sidebar.skills", { defaultValue: "Skills / Agentes" })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("skillAgent.fichaList.subtitle")}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                onClick={() => navigate("/skills/tareas")}
              >
                {t("skillAgent.fichaList.tasks")}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddOutlinedIcon />}
                sx={pagePrimaryButtonSx}
                onClick={() => navigate("/skills/new")}
              >
                {t("skillAgent.fichaList.newCard")}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.info.main, 0.04),
          borderColor: alpha(theme.palette.info.main, 0.2),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <HelpOutlineOutlinedIcon
            color="info"
            fontSize="small"
            sx={{ mt: 0.3 }}
          />
          <Typography variant="body2" color="text.secondary">
            <Trans
              i18nKey="skillAgent.fichaList.designHint"
              components={{ b: <b /> }}
            />
          </Typography>
        </Stack>
      </Paper>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mt: 2, mb: 2 }}
      >
        <TextField
          size="small"
          placeholder={t("skillAgent.fichaList.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ ...pageFieldSx, minWidth: 280, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="outlined" onClick={load}>
          {t("skillAgent.fichaList.search")}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">{t("skillAgent.fichaList.empty")}</Alert>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
              >
                <TableCell>{t("skillAgent.fichaList.colCard")}</TableCell>
                <TableCell>{t("skillAgent.fichaList.colClient")}</TableCell>
                <TableCell>{t("skillAgent.fichaList.colCategory")}</TableCell>
                <TableCell>{t("skillAgent.fichaList.colStatus")}</TableCell>
                <TableCell align="right">
                  {t("skillAgent.fichaList.colActions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const st = statusDisplay(row);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/skills/${row.id}`)}
                  >
                    <TableCell>
                      <Typography fontWeight={700}>
                        {row.Titulo || t("skillAgent.fichaList.untitled")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.TemporadaRef || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.CustomerName || row.MarcaName || "—"}
                    </TableCell>
                    <TableCell>{row.CategoriaFoco || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t(st.labelKey, {
                          name:
                            st.name || t("skillAgent.fichaList.status.someone"),
                        })}
                        color={st.color}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell
                      align="right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/skills/${row.id}`)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
